import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  IndexFirstLinkedinChat,
  IndexFirstLinkedinChatAfterInvitation,
  mapTaskTypeToTemplateType,
} from 'utils/businessLogic';
import SequenceInstanceTaskCompletedComponent from './sequenceInstanceTaskCompletedComponent';
import SequenceInstanceTaskComponent from './sequenceInstanceTaskComponent';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';
import { SequenceInstance } from 'crono-fe-common/types/sequenceInstance';
import useJumpToSequenceInstanceStep from 'hooks/services/strategy/useJumpToSequenceInstanceStep';
import { FeConstants } from 'constants/FeConstants';
import { useAnalytics } from 'context/analytics';
import useAddSequenceInstanceStep from 'hooks/services/task/useAddSequenceInstanceStep';
import { colors } from 'crono-fe-common/theme';
import { useDeleteTaskWithConfirm } from 'hooks/services/task/useDeleteTask';
import usePatchTask, {
  useSkipTaskWithConfirm,
} from 'hooks/services/task/usePatchTask';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { Template } from 'crono-fe-common/types/template';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { TaskPatch } from 'crono-fe-common/types/DTO/taskPatch';
import { ConfirmEdit } from 'hooks/strategy/useOnBehalfEditConfirm';
import usePersonalizationIdentity from 'hooks/strategy/usePersonalizationIdentity';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import TemplatesTooltipCard from 'pages/accountDrawer/emailView/TemplatesTooltipCard';
import StrategyXarrow from 'context/strategyDetail/strategyXarrow';
import { addBusinessDays, format } from 'date-fns';
import useGetEmailMailbox from 'hooks/services/email/useGetEmailMailbox';
import { Mailbox } from 'crono-fe-common/types/mailbox';
import SequenceAssignedMailboxCard from './SequenceAssignedMailboxCard';
import { cn } from 'utils/style-utils';

interface SequenceInstanceContentProps {
  sequenceInstance: SequenceInstance;
  prospectId: string | undefined;
  onClose: () => void;
  handleOpenDateCalendar: (task: TaskTodo) => void;
  taskOrder?: number;
  allowToPersonalise?: boolean;
  allowToOpenInsights?: boolean;
  disableScrollToTask?: boolean;
  className?: string;
  //Edits this sequence as its owner, without changing ownership. Manager only
  onBehalf?: boolean;
  //The sequence belongs to another user, so actions the API cannot perform on
  //their behalf are withheld. Distinct from onBehalf, which is only a wire flag
  sequenceNotOwned?: boolean;
  //Gates every edit; defaults to applying it straight away
  confirmEdit?: ConfirmEdit;
}

interface ArrowsForNotCompletedStepProps {
  gridBreak: string;
  index: number;
  keyArrow: number;
  prevTask: TaskTodo | null;
  task: TaskTodo;
}

const ArrowsForNotCompletedStep = ({
  gridBreak,
  index,
  keyArrow,
  prevTask,
  task,
}: ArrowsForNotCompletedStepProps) => {
  return (
    <>
      <StrategyXarrow
        key={`${index}-1-${keyArrow}-${prevTask ? prevTask.id.toString() : 'delay' + index}`}
        gridBreak={gridBreak}
        //If the previous task is null we reverse it, so from the detail to the delay, the other way around causes issues
        start={prevTask ? prevTask.id.toString() : task.id.toString()}
        end={prevTask ? task.id.toString() : 'delay' + index}
        color={colors.grey33}
        zIndex={-1}
      />
      {prevTask &&
      (prevTask.strategyStepConditional || task.strategyStepConditional) ? (
        <StrategyXarrow
          key={`${index}-2-${keyArrow}-${prevTask.id.toString()}`}
          gridBreak={gridBreak}
          start={
            prevTask.strategyStepConditional &&
            prevTask.active &&
            !prevTask.completed
              ? `${prevTask.strategyStepConditional.id}-conditional`
              : prevTask.id.toString()
          }
          end={
            task.strategyStepConditional
              ? `${task.strategyStepConditional.id.toString()}-conditional`
              : task.id.toString()
          }
          color={colors.grey33}
        />
      ) : null}
    </>
  );
};

const SequenceInstanceContent = ({
  sequenceInstance,
  prospectId,
  onClose,
  handleOpenDateCalendar,
  taskOrder,
  allowToPersonalise,
  allowToOpenInsights,
  className,
  onBehalf,
  sequenceNotOwned,
  confirmEdit = (applyEdit) => applyEdit(),
}: SequenceInstanceContentProps) => {
  const sequenceInstanceId = sequenceInstance.id;

  /**
   * Derived from the sequence being rendered rather than from the strategy
   * context, which outlives a single sequence: it wraps every home route, so on
   * the task board it still holds whatever was last opened in Personalize.
   */
  const personalization = usePersonalizationIdentity(sequenceInstance);

  const analytics = useAnalytics();

  const { mutate: patchTaskMutation, error: errorPatchTask } = usePatchTask();

  /**
   * Wrapping the mutation rather than each of the dozen call sites keeps the
   * on-behalf flag and the confirmation impossible to forget on a new edit.
   *
   * `onApply` carries everything that must not happen when the confirmation is
   * dismissed: the in-place edit of the task object, which would otherwise
   * leave a cancelled change showing, and the analytics event, which would
   * otherwise report an edit that never reached the API.
   */
  const patchTask = (taskPatch: TaskPatch, onApply?: () => void) =>
    confirmEdit(() => {
      onApply?.();
      patchTaskMutation({ ...taskPatch, onBehalf });
    });

  const [stepTemplateIndex, setStepTemplateIndex] = useState<number | null>(
    null,
  );

  //To get the correct type of template to open if we are picking for a conditionalStep
  const [conditionalStepTemplateType, setConditionalStepTemplate] =
    useState<SwitchToTask | null>(null);

  const onAdd = (
    templateId: number,
    conditionalStepTemplateType: SwitchToTask | null,
  ) => {
    if (stepTemplateIndex === null) return;
    //If I'm changing a conditionalStep and not the original one
    if (conditionalStepTemplateType) {
      return;
    }
    patchTask(
      {
        id: sequenceInstance.tasks[stepTemplateIndex].id,
        templateId: templateId,
      },
      () => {
        if (analytics) {
          analytics.track('patch-task', {
            type: 'template',
          });
        }
      },
    );

    setStepTemplateIndex(null);
    setSequenceInstanceStepTemplateId(null);
    setConditionalStepTemplate(null);
  };

  const {
    mutate: jumpToStep,
    isSuccess: successJump,
    error: errorJumpToSequenceInstanceStep,
  } = useJumpToSequenceInstanceStep();

  const handleJumpToStep = (index: number) => {
    if (!prospectId) return;
    if (
      sequenceInstance.id !== 0 &&
      sequenceInstance.tasks[index].order !== null
    ) {
      jumpToStep({
        prospectId: prospectId,
        sequenceInstanceId: sequenceInstance.id,
        step: sequenceInstance.tasks[index].order!,
      });
      if (analytics) {
        analytics.track('jump-to-step', {});
      }
    }
  };

  const { deleteTaskWithConfirm: handleDeleteTask, error: errorDeleteTask } =
    useDeleteTaskWithConfirm(() => {
      if (analytics) {
        analytics.track('patch-task', {
          type: 'delete',
        });
      }
    });

  const handleChangeDelay = (increment: number, index: number) => {
    //Copy the array of steps
    const tasks = [...(sequenceInstance.tasks || [])];
    //Update the step
    if (tasks[index].delay === null) return;
    const nextDelay = tasks[index].delay! + increment;
    if (
      nextDelay < FeConstants.MIN_DELAY ||
      nextDelay > FeConstants.MAX_DELAY
    ) {
      return;
    }

    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          delay: nextDelay,
        },
        () => {
          tasks[index].delay = nextDelay;

          if (analytics) {
            analytics.track('patch-task', {
              type: 'delay',
            });
          }
        },
      );
    }
  };

  const handleChangeAutomatic = (automatic: boolean, index: number) => {
    //Copy the array of steps
    const tasks = [...(sequenceInstance.tasks || [])];
    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          automatic,
        },
        () => {
          tasks[index].automatic = automatic;

          if (analytics) {
            analytics.track('patch-task', {
              type: 'automatic',
            });
          }
        },
      );
    }
  };

  const handleChangeStepType = (
    newType: { type: TaskTodoType; subtype: TaskTodoSubtype },
    index: number,
  ) => {
    //If the type is the same, we don't do anything
    if (newType.type === sequenceInstance.tasks[index].type) {
      if (
        newType.type !== TaskTodoType.LINKEDIN ||
        newType.subtype === sequenceInstance.tasks[index].subtype
      )
        return;
    }
    //Copy the array of tasks
    const tasks = [...(sequenceInstance.tasks || [])];

    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          type: newType.type,
          subtype: newType.subtype,
          //A step of a new type is never automatic, and its template is cleared
          automatic: false,
          templateId: undefined,
        },
        () => {
          tasks[index].type = newType.type;
          tasks[index].subtype = newType.subtype;
          tasks[index].automatic = false;
          tasks[index].template = null;
          tasks[index].templateId = null;

          if (analytics) {
            analytics.track('patch-task', {
              type: 'type',
            });
          }
        },
      );
    }
  };

  const handleChangeTime = (time: Date, index: number) => {
    //Copy the array of tasks
    const tasks = [...(sequenceInstance.tasks || [])];

    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          activityDate: time,
        },
        () => {
          tasks[index].activityDate = time;

          if (analytics) {
            analytics.track('patch-task', {
              type: 'schedule-time',
            });
          }
        },
      );
    }
  };

  const handleChangeReplyToThread = (replyToThread: boolean, index: number) => {
    if (sequenceInstance) {
      patchTask(
        {
          id: sequenceInstance.tasks[index].id,
          replyToThread: replyToThread,
        },
        () => {
          if (analytics) {
            analytics.track('patch-task', {
              type: 'replyToThread',
            });
          }
        },
      );
    }
  };

  const { skipTaskWithConfirm: handleSkipTask } = useSkipTaskWithConfirm(() => {
    if (analytics) {
      analytics.track('patch-task', {
        type: 'skip',
      });
    }
  });

  const handleChangeCc = (ccAddresses: string[], index: number) => {
    const tasks = [...(sequenceInstance.tasks || [])];

    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          ccAddresses,
        },
        () => {
          tasks[index].ccAddresses = ccAddresses;

          if (analytics) {
            analytics.track('patch-task', {
              type: 'ccAddresses',
            });
          }
        },
      );
    }
  };

  const handleChangeSubject = (subject: string, index: number) => {
    const tasks = [...(sequenceInstance.tasks || [])];
    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          subject,
        },
        () => {
          tasks[index].subject = subject;
        },
      );
    }
  };

  const handleChangeDescription = (description: string, index: number) => {
    const tasks = [...(sequenceInstance.tasks || [])];
    if (sequenceInstance) {
      patchTask(
        {
          id: tasks[index].id,
          description,
        },
        () => {
          tasks[index].description = description;
        },
      );
    }
  };

  const handleAllChanges = (type: string, payload: unknown, index: number) => {
    switch (type) {
      case 'delay':
        handleChangeDelay(payload as number, index);
        break;
      case 'automatic':
        handleChangeAutomatic(payload as boolean, index);
        break;
      case 'type':
        handleChangeStepType(
          payload as { type: TaskTodoType; subtype: TaskTodoSubtype },
          index,
        );
        break;
      case 'delete':
        handleDeleteTask(sequenceInstance.tasks[index].id);
        break;
      case 'scheduleTime':
        handleChangeTime(payload as Date, index);
        break;
      case 'replyToThread':
        handleChangeReplyToThread(payload as boolean, index);
        break;
      case 'skip':
        //Skipping already asks for its own confirmation, so it is not put
        //behind the on-behalf one as well
        handleSkipTask(sequenceInstance.tasks[index].id, onBehalf);
        break;
      case 'ccAddresses':
        handleChangeCc(payload as string[], index);
        break;
      case 'subject':
        handleChangeSubject(payload as string, index);
        break;
      case 'description':
        handleChangeDescription(payload as string, index);
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (successJump) {
      onClose();
    }
  }, [successJump]);

  const {
    mutate: addSequenceInstanceStep,
    isSuccess: stepAdded,
    error: errorAddingStep,
  } = useAddSequenceInstanceStep();

  const handleAddStep = (index: number) => {
    addSequenceInstanceStep({
      sequenceInstanceId: sequenceInstanceId,
      step: index + 1,
      data: {
        type: TaskTodoType.EMAIL,
        automatic: false,
        delay: 1,
        templateId: null,
      },
    });
    if (analytics) {
      analytics.track('patch_sequence_instance', {
        patch: 'add_task',
        sequenceInstanceId: sequenceInstanceId,
      });
    }
  };

  const indexFirstLinkedin = useMemo(() => {
    return IndexFirstLinkedinChat(sequenceInstance.tasks ?? []);
  }, [sequenceInstance.tasks]);

  const handleTemplatePicker = (type: SwitchToTask) => {
    setConditionalStepTemplate(type);
  };

  const [sequenceInstanceStepTemplateId, setSequenceInstanceStepTemplateId] =
    useState<number | null>(null);

  const currentTemplateType =
    useMemo(() => {
      if (stepTemplateIndex === null) return TemplateType.EMAIL;
      const taskType = sequenceInstance.tasks[stepTemplateIndex].type;
      const taskSubtype = sequenceInstance.tasks[stepTemplateIndex].subtype;
      return mapTaskTypeToTemplateType(taskType, taskSubtype);
    }, [stepTemplateIndex, sequenceInstance.tasks]) ?? TemplateType.EMAIL;

  const memoizedTemplateComponent = useMemo(() => {
    if (stepTemplateIndex !== null) {
      return (
        <TemplatesTooltipCard
          selectedTemplateId={
            conditionalStepTemplateType
              ? (sequenceInstance.tasks[stepTemplateIndex]
                  .strategyStepConditional?.template?.id ?? null)
              : (sequenceInstance.tasks[stepTemplateIndex].template?.id ?? null)
          }
          setSelectedTemplate={(template: Template) =>
            onAdd(template.id, conditionalStepTemplateType)
          }
          hideShared={false}
          hideAll={false}
          selectedType={currentTemplateType}
          showAnalytics={true}
          close={() => {
            setStepTemplateIndex(null);
            setConditionalStepTemplate(null);
            setSequenceInstanceStepTemplateId(null);
          }}
          strategyStepTemplateId={sequenceInstanceStepTemplateId}
          visibility={'visible'}
        />
      );
    } else {
      return null;
    }
  }, [stepTemplateIndex]);

  //Since the arrows are declared at this level they do not notice if the child component changes, so we need to make it trigger
  //The ping from child is called everytime the child component changes

  const [keyArrow, setKeyArrow] = useState<number>(0);
  const [tasksContainer, setTasksContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [isTasksContainerFullyVisible, setIsTaskContainerFullyVisible] =
    useState<boolean>(false);
  const onRefChange = useCallback((node: HTMLDivElement) => {
    if (!node) return;
    setTasksContainer(node);
    const resizeObserver = new ResizeObserver(() => {
      setKeyArrow(Math.random() * 100000);
      const parent = node.parentElement;
      if (parent) {
        setIsTaskContainerFullyVisible(
          parent.scrollHeight === parent.clientHeight,
        );
      }
    });
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const [completedSteps, todoSteps, allSteps] = (() => {
    const completedSteps: ReactNode[] = [];
    const todoSteps: ReactNode[] = [];
    const allSteps: ReactNode[] = [];
    const tasks = sequenceInstance.tasks ?? [];
    const indexFirstLinkedinAfterInvitation =
      IndexFirstLinkedinChatAfterInvitation(tasks);

    if (tasks.length > 0) {
      const firstStepWithConditionalIndex = tasks.findIndex(
        (t) => t.strategyStepConditional && !t.completed,
      );
      const tasksContainsConditionals = firstStepWithConditionalIndex !== -1;
      let conditionalTaskActivityDate: Date | null = null;
      const anchorTask = tasks[firstStepWithConditionalIndex];
      if (anchorTask?.activityDate != null) {
        const anchorDate = new Date(anchorTask.activityDate);
        if (!isNaN(anchorDate.getTime())) {
          conditionalTaskActivityDate = addBusinessDays(
            anchorDate,
            -1 * (anchorTask.delay ?? 0),
          );
        }
      }

      sequenceInstance.tasks.forEach((task, index, tasks) => {
        const prevTask = index !== 0 ? tasks[index - 1] : null;
        const isPrevTaskCompleted =
          (prevTask?.completed || prevTask?.skipped) ?? false;
        const conditionalAfterInvitation =
          prevTask?.subtype === TaskTodoSubtype.LinkedinInvitation &&
          !!task.strategyStepConditional;

        if (conditionalTaskActivityDate) {
          if (task.strategyStepConditional) {
            conditionalTaskActivityDate = addBusinessDays(
              conditionalTaskActivityDate,
              task.strategyStepConditional.daysAfterConditionNotAccepted,
            );
          } else {
            if (index > firstStepWithConditionalIndex) {
              conditionalTaskActivityDate = addBusinessDays(
                conditionalTaskActivityDate,
                task.delay ?? 0,
              );
            }
          }
        }

        if (task.completed) {
          const isLinkedinInvitation =
            task.type === TaskTodoType.LINKEDIN &&
            task.subtype === TaskTodoSubtype.LinkedinInvitation;
          const component = (
            <>
              <SequenceInstanceTaskCompletedComponent
                key={`${index}-${sequenceInstanceId}`}
                task={task}
                index={index}
                showConditionalFlowButton={
                  tasksContainsConditionals && isLinkedinInvitation
                }
                linkedinUntilAcceptedDays={
                  isLinkedinInvitation
                    ? tasks[indexFirstLinkedinAfterInvitation]
                        ?.strategyStepConditional?.linkedinUntilAcceptedDays
                    : undefined
                }
                prospect={sequenceInstance.prospect ?? null}
                personalization={personalization}
              />
              {prevTask &&
                (isPrevTaskCompleted ? (
                  <StrategyXarrow
                    key={`${index}-1-${keyArrow}-${prevTask.id.toString()}`}
                    gridBreak={'100%-90'}
                    //Reversed to make it work better, the oder way around was creating too much space at the end of the sequenceInstanceContent (empty scroll)
                    start={task.id.toString()}
                    end={prevTask.id.toString()}
                    color={colors.grey33}
                    zIndex={-1}
                  />
                ) : (
                  <ArrowsForNotCompletedStep
                    gridBreak="100%-20"
                    index={index}
                    keyArrow={keyArrow}
                    prevTask={prevTask}
                    task={task}
                  />
                ))}
            </>
          );
          completedSteps.push(component);
          allSteps.push(component);
        } else {
          const component = (
            <>
              <SequenceInstanceTaskComponent
                allowToOpenInsights={allowToOpenInsights}
                key={`${index}-${sequenceInstanceId}`}
                allowToPersonalise={allowToPersonalise}
                onBehalf={onBehalf}
                sequenceNotOwned={sequenceNotOwned}
                confirmEdit={confirmEdit}
                personalization={personalization}
                task={task}
                rewriteError={sequenceInstance.rewriteError ?? null}
                linkedinUntilAcceptedDays={
                  tasks[indexFirstLinkedinAfterInvitation]
                    ?.strategyStepConditional?.linkedinUntilAcceptedDays
                }
                index={index}
                handleChange={handleAllChanges}
                handleJumpToStep={handleJumpToStep}
                openTemplatePicker={(index: number) => {
                  setStepTemplateIndex(index);
                  setConditionalStepTemplate(null);
                }}
                handleTemplatePicker={(type: SwitchToTask) => {
                  handleTemplatePicker(type);
                  setStepTemplateIndex(index);
                }}
                openDateCalendar={handleOpenDateCalendar}
                firstTask={index === sequenceInstance.actualStep}
                opened={taskOrder ? task.order === taskOrder : false}
                handleRemoveTemplate={(index: number) => {
                  if (sequenceInstance.tasks[index].id) {
                    const step = sequenceInstance.tasks[index];
                    const newAutomatic =
                      step.type !== TaskTodoType.LINKEDIN ||
                      step.subtype !== TaskTodoSubtype.LinkedinInvitation;
                    patchTask({
                      id: sequenceInstance.tasks[index].id,
                      templateId: -1,
                      ...(step.automatic ? { automatic: !newAutomatic } : {}),
                    });
                    if (analytics) {
                      analytics.track('patch-task', {
                        type: 'template',
                      });
                    }
                  }
                }}
                tasks={tasks}
                setSequenceInstanceStepTemplateId={
                  setSequenceInstanceStepTemplateId
                }
                templateComponent={memoizedTemplateComponent}
                showTemplateTab={stepTemplateIndex === index}
                closeTemplateTooltip={() => {
                  setStepTemplateIndex(null);
                  setConditionalStepTemplate(null);
                  setSequenceInstanceStepTemplateId(null);
                }}
                taskConditionalActivityDate={
                  conditionalTaskActivityDate
                    ? format(conditionalTaskActivityDate, 'MMM d, yyyy')
                    : undefined
                }
                onClose={onClose}
              />
              <ArrowsForNotCompletedStep
                gridBreak={conditionalAfterInvitation ? '50%' : '100%-90'}
                index={index}
                keyArrow={keyArrow}
                prevTask={prevTask}
                task={task}
              />
            </>
          );
          todoSteps.push(component);
          allSteps.push(component);
        }
      });
    }

    return [completedSteps, todoSteps, allSteps];
  })();

  useConditionalSnackBar([
    {
      condition: !!stepAdded,
      message: 'Step added correctly',
      severity: 'success',
    },
    {
      condition: !!errorAddingStep,
      message: getError(errorAddingStep) ?? 'Error adding the step',
      severity: 'error',
    },
    {
      condition: !!successJump,
      message: 'Jump to the step correctly performed',
      severity: 'success',
    },
    {
      condition: !!errorJumpToSequenceInstanceStep,
      message:
        getError(errorJumpToSequenceInstanceStep) ??
        'Error jumping to the step',
      severity: 'error',
    },
    {
      condition: !!errorPatchTask,
      message: getError(errorPatchTask) ?? 'Error patching the task',
      severity: 'error',
    },
    {
      condition: !!errorDeleteTask,
      message: getError(errorDeleteTask) ?? 'Error deleting the task',
      severity: 'error',
    },
  ]);

  /**
   * The type comes from the caller's own mailboxes, so it can only be known for
   * a sequence they own — the card renders the address alone otherwise, rather
   * than disappearing on every sequence belonging to somebody else. It waits for
   * the mailboxes either way, so the address does not shift sideways when the
   * icon appears next to it.
   */
  const { data: emailMailboxesDb, isLoading: isGetEmailMailboxLoading } =
    useGetEmailMailbox(false);

  const selectedMailbox: Mailbox | null = useMemo(() => {
    if (!sequenceInstance.selectedEmail) return null;
    return (
      emailMailboxesDb?.data.data?.find(
        (mailbox) => mailbox.email === sequenceInstance.selectedEmail,
      ) ?? null
    );
  }, [emailMailboxesDb, sequenceInstance.selectedEmail]);

  return (
    <div
      className={cn(
        'relative flex flex-col pb-5 pr-1.5 justify-start overflow-x-hidden overflow-y-auto',
        className,
      )}
    >
      <div ref={onRefChange} className="todo-steps" id="todo-steps-div">
        {sequenceInstance.selectedEmail && !isGetEmailMailboxLoading && (
          <SequenceAssignedMailboxCard
            email={sequenceInstance.selectedEmail}
            integration={selectedMailbox?.type}
          />
        )}

        {allSteps}
      </div>
    </div>
  );
};

export default SequenceInstanceContent;
